/**
 * `@crown/ui/i18n`: the shared package's own catalogue and the browser
 * runtime of `@crown/i18n`, so an app configures both in one call:
 *
 *   configureI18n({
 *     catalogues: mergeCatalogueSets(uiCatalogues, appCatalogues),
 *     context: resolveBrowserContext({ user, organization, workspace }, uiCatalogues),
 *   });
 *
 * Components read their text through `translate` and mark it with `data-i18n`, so
 * they render English before any app configures a locale and re-render when
 * one does.
 */
import {
  availableLocales,
  type CatalogueSet,
  type I18nContextInput,
  type MessageArgs,
  mergeCatalogueSets,
} from '@crown/i18n';
import { hasGlobal, isObjectLike, isString } from '@crown/framework/ir';
import coreEnGB from '@crown/i18n/locales/en-GB.json';
import coreClinical from '@crown/i18n/locales/en-GB.clinical.json';
import coreEducation from '@crown/i18n/locales/en-GB.education.json';
import coreSurvey from '@crown/i18n/locales/en-GB.survey.json';
import coreEnUS from '@crown/i18n/locales/en-US.json';
import coreCy from '@crown/i18n/locales/cy.json';
import coreCyClinical from '@crown/i18n/locales/cy.clinical.json';
import coreCyEducation from '@crown/i18n/locales/cy.education.json';
import coreCySurvey from '@crown/i18n/locales/cy.survey.json';
import coreGd from '@crown/i18n/locales/gd.json';
import coreGdClinical from '@crown/i18n/locales/gd.clinical.json';
import coreGdEducation from '@crown/i18n/locales/gd.education.json';
import coreGdSurvey from '@crown/i18n/locales/gd.survey.json';
import coreAr from '@crown/i18n/locales/ar.json';
import coreArClinical from '@crown/i18n/locales/ar.clinical.json';
import coreArEducation from '@crown/i18n/locales/ar.education.json';
import coreArSurvey from '@crown/i18n/locales/ar.survey.json';
import {
  addCatalogues,
  applyDocumentLocale,
  bindI18n,
  configureI18n,
  localeDisplayName,
  registeredCatalogues,
  rememberLocale,
  resolveBrowserContext,
  setLocale,
  setUseCase,
  translate,
} from '@crown/i18n/ui';
import ar from '../locales/ar.json';
import cy from '../locales/cy.json';
import enGB from '../locales/en-GB.json';
import gd from '../locales/gd.json';

/** The settings an app knows about the caller: the same shape the server middleware takes. */
export type I18nSettings = Omit<I18nContextInput, 'requested' | 'available'>;

export type UiMessageKey = keyof typeof enGB | keyof typeof coreEnGB;

/** `common.*`, `validation.*` and `term.*` from `@crown/i18n` plus this package's `ui.*`. */
export const uiCatalogues: CatalogueSet = mergeCatalogueSets(
  {
    'en-GB': coreEnGB,
    'en-GB.clinical': coreClinical,
    'en-GB.survey': coreSurvey,
    'en-GB.education': coreEducation,
    'en-US': coreEnUS,
    cy: coreCy,
    'cy.clinical': coreCyClinical,
    'cy.survey': coreCySurvey,
    'cy.education': coreCyEducation,
    gd: coreGd,
    'gd.clinical': coreGdClinical,
    'gd.survey': coreGdSurvey,
    'gd.education': coreGdEducation,
    ar: coreAr,
    'ar.clinical': coreArClinical,
    'ar.survey': coreArSurvey,
    'ar.education': coreArEducation,
  },
  { 'en-GB': enGB, cy, gd, ar },
);

addCatalogues(uiCatalogues);

export {
  addCatalogues,
  applyDocumentLocale,
  bindI18n,
  configureI18n,
  i18nSnapshot,
  type I18nSnapshot,
  isI18nConfigured,
  locale,
  localeDisplayName,
  rememberLocale,
  rememberedLocale,
  renderI18n,
  resolveBrowserContext,
  setLocale,
  setUseCase,
  translate,
  translator,
  useCase,
} from '@crown/i18n/ui';
export { mergeCatalogueSets };

/** `data-i18n` attributes for a template literal, so the binder keeps the text current. */
export function i18nAttr(
  key: UiMessageKey | (string & {}),
  args?: MessageArgs,
): string {
  const params = args
    ? ` data-i18n-params='${JSON.stringify(args).replaceAll("'", '&#39;')}'`
    : '';
  return `data-i18n="${key}"${params}`;
}

/** Text plus its `data-i18n` marker, for element construction outside a template. */
export function localise(
  element: Element,
  key: UiMessageKey | (string & {}),
  args?: MessageArgs,
): void {
  element.setAttribute('data-i18n', key);
  if (args) element.setAttribute('data-i18n-params', JSON.stringify(args));
  element.textContent = translate(key, args);
}

export interface StartI18nOptions {
  /** The app's own catalogues, merged over the shared ones. */
  readonly catalogues?: CatalogueSet | undefined;
  /** What is already known about the caller; usually nothing before sign-in. */
  readonly settings?: I18nSettings | undefined;
  readonly timeZone?: string | undefined;
}

let started: CatalogueSet | undefined;

/**
 * One call at app boot: configures the translator from the browser's
 * languages and whatever settings are known, sets `<html lang dir>` and
 * binds `data-i18n` markup on the page. Returns the merged catalogues so a
 * later `adoptI18nSettings` resolves against the same set.
 */
export function startI18n(options: StartI18nOptions = {}): CatalogueSet {
  // What the app registered as its modules loaded (its own messages, added
  // before boot) is kept: configuring with only the kit's catalogues threw it
  // away, and every one of the app's labels showed as its key.
  const base = mergeCatalogueSets(uiCatalogues, registeredCatalogues());
  const catalogues = options.catalogues ? mergeCatalogueSets(base, options.catalogues) : base;
  started = catalogues;
  configureI18n({
    catalogues,
    context: resolveBrowserContext(options.settings ?? {}, catalogues),
    timeZone: options.timeZone,
  });
  applyDocumentLocale();
  if (hasGlobal('document')) bindI18n(document.body);
  return catalogues;
}

/**
 * Re-resolves once the session, organisation or workspace is known: the
 * person's own locale, the workspace's use case, the organisation's country.
 */
export function adoptI18nSettings(settings: I18nSettings): void {
  const context = resolveBrowserContext(settings, started ?? uiCatalogues);
  setLocale(context.locale);
  setUseCase(context.useCase);
  applyDocumentLocale();
}

/** The auth client members the session adoption reads; every one optional so any app's client fits. */
export interface I18nSessionClient {
  readonly workspace?: {
    readonly get?: (input: {
      workspaceId: string;
    }) => Promise<{ data?: unknown }>;
  };
  readonly organization?: {
    readonly getFullOrganization?: (input: {
      query: { organizationId: string };
    }) => Promise<{ data?: unknown }>;
  };
}

export interface I18nSession {
  /** The session's user; clients that never declared `locale` still fit. */
  readonly user?: object | null | undefined;
  readonly session?:
    | {
        readonly activeOrganizationId?: string | null | undefined;
        readonly activeWorkspaceId?: string | null | undefined;
      }
    | null
    | undefined;
}

/** The three wording fields a workspace or organisation record may carry. */
interface WordingRecord {
  readonly locale?: unknown;
  readonly defaultLocale?: unknown;
  readonly useCase?: unknown;
  readonly countryCode?: unknown;
}

function asWordingRecord<T>(value: T): WordingRecord | undefined {
  return isObjectLike(value) ? value : undefined;
}

function asTag<T>(value: T): string | undefined {
  return isString(value) && value !== '' ? value : undefined;
}

/**
 * Reads the wording settings behind a session: the person's locale from the
 * session, the workspace overrides and the organisation defaults from their
 * records. A fetch that fails leaves that layer empty rather than the page
 * without text.
 */
export async function loadI18nSettings(
  client: I18nSessionClient,
  session: I18nSession | null | undefined,
): Promise<I18nSettings> {
  const workspaceId = session?.session?.activeWorkspaceId;
  const organizationId = session?.session?.activeOrganizationId;
  const [workspace, organization] = await Promise.all([
    workspaceId && client.workspace?.get
      ? client.workspace.get({ workspaceId }).then(
          (r) => r.data,
          () => undefined,
        )
      : undefined,
    organizationId && client.organization?.getFullOrganization
      ? client.organization
          .getFullOrganization({ query: { organizationId } })
          .then(
            (r) => r.data,
            () => undefined,
          )
      : undefined,
  ]);
  const ws = asWordingRecord(workspace);
  const org = asWordingRecord(organization);
  const user = asWordingRecord(session?.user);
  return {
    user: user && { locale: asTag(user.locale) },
    workspace: ws && {
      locale: asTag(ws.locale),
      useCase: asTag(ws.useCase),
      countryCode: asTag(ws.countryCode),
    },
    organization: org && {
      defaultLocale: asTag(org.defaultLocale),
      useCase: asTag(org.useCase),
      countryCode: asTag(org.countryCode),
    },
  };
}

/** `loadI18nSettings` then `adoptI18nSettings`: the one call an app makes once signed in. */
export async function adoptSessionI18n(
  client: I18nSessionClient,
  session: I18nSession | null | undefined,
): Promise<void> {
  adoptI18nSettings(await loadI18nSettings(client, session));
}

/**
 * The auth client member `switchLocale` writes through. `$fetch` is what every
 * Better Auth client exposes, and it carries the session cookie; the typed
 * `updateUser` would need each app's client to declare the `locale` field.
 */
export interface LocaleUpdateClient {
  /** Whatever it resolves to is not read: the write either lands or throws. */
  readonly $fetch: (
    path: string,
    init: { method: 'POST'; body: Record<string, string> },
  ) => Promise<object | void>;
}

export interface SwitchLocaleOptions {
  readonly client?: LocaleUpdateClient | undefined;
  /** Reload after switching (the default) so text assembled at module scope re-evaluates too. */
  readonly reload?: boolean | undefined;
}

/**
 * The person picks a language: it is remembered in this browser, written to
 * their account when one is signed in, applied to the live translator, and
 * the page reloads so every string, including copy built once at module
 * scope, comes back in the new language.
 */
export async function switchLocale(
  next: string,
  options: SwitchLocaleOptions = {},
): Promise<void> {
  rememberLocale(next);
  const context = resolveBrowserContext(
    { user: { locale: next } },
    registeredCatalogues(),
  );
  setLocale(context.locale);
  applyDocumentLocale();
  if (options.client) {
    try {
      await options.client.$fetch('/update-user', {
        method: 'POST',
        body: { locale: next },
      });
    } catch (error) {
      console.warn(
        '[i18n] the locale was applied here but not saved to the account',
        error,
      );
    }
  }
  if (options.reload !== false && hasGlobal('location'))
    location.reload();
}

export function availableUiLocales(): string[] {
  return availableLocales(registeredCatalogues());
}
